import { isAxiosError } from "axios";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/Api";
import { queryKeyBuildings } from "@smartfm/container/hooks/useBuildingIds";
import { queryKeyClient, queryKeyRecordId } from "@smartfm/container/hooks/useClientId";
import { getAdvancedNavigationTree } from '@smartfm/container/hooks/useAdvancedNavigationTree';
import { setDefaultDashboard } from "@/services/DashboardService";

export const useDefaultData = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  return useQuery({
    queryFn: async () => {
      let defaultClient = null;
      const defaultBuildingsMap = new Map<string, { site: string; default: boolean }>();

      try {
        const defaultSiteResponse = await api.get("admin/getDefaultSiteForUser");

        for (const item of defaultSiteResponse.data.data) {
          if (item.site) {
            defaultBuildingsMap.set(item.client, { site: item.site, default: true } );
          } else if (item.default) {
            const tree = await getAdvancedNavigationTree({ clientId: item.client });
            defaultBuildingsMap.set(item.client, { site: tree?.data?.itemsOrder[0] || '', default: false } );
          }
          if (item.default) {
            defaultClient = item.client;
          }
        }
        
        if (!defaultClient) {
          throw new Error('No default client or site found');
        }
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          throw new Error(error.response.data.data);
        }
        try {
          const defaultData = await getDefaultClientWithBuildings();
          if (defaultData) {
            defaultBuildingsMap.set(defaultData.clientId, { site: defaultData.buildingId, default: true });
            setDefaultDashboard(defaultData.clientId, defaultData.buildingId, true);
          }
        } catch (e) {
          console.error('Error getting default client with buildings:', e);
        }
      } finally {
        if (!searchParams.has(queryKeyClient) && defaultClient) {
          // backward compatibility
          searchParams.set(queryKeyRecordId, defaultClient);
          searchParams.set(queryKeyClient, defaultClient);
          if (defaultBuildingsMap.has(defaultClient)) {
            searchParams.set(queryKeyBuildings, defaultBuildingsMap.get(defaultClient)?.site!);
          } 
          setSearchParams(searchParams);
        }
      }
      return {
        defaultClient,
        defaultBuildingsMap,
      }
    },

    queryKey: ["getDefaultSiteForUser"],
  });
};

const getDefaultClientWithBuildings = async () => {
  const { data } = await api.get('/navigation/projects');
  for (const client of data.data) {
    const buildings = await getAdvancedNavigationTree({ clientId: client.id });
    if (buildings?.data.totalItemsCount) {
      return {
        clientId: client.id,
        buildingId: buildings.data.itemsOrder[0],
      };
    }
  }
};